import Store from "../Store";
import { computed } from "mobx";
import { FillPaint, SymbolLayout, SymbolPaint } from "mapbox-gl";
import chroma from "chroma-js";
import PaintBase from "./PaintBase";
import GemeentePaint from "./GemeentePaint";
import ProvinciePaint from "./ProvinciePaint";
import RegioPaint from "./RegioPaint";
import { View } from "../Store";

export type Paint = {
  fillPaint?: FillPaint;
  symbolPaint?: SymbolPaint;
  symbolLayout?: SymbolLayout;
};

export type ScaleType = "quantile" | "linear";

export type ScaleOption = {
  label: string;
  type: ScaleType;
};

export type ColorScale = (string | number)[];

export default class PaintStore {
  store: Store;
  static colors = ["#fef0d9", "#fed093", "#fdb77a", "#fd9b64", "#ec603f", "#c92116", "#c92116"];
  static scaleOptions: ScaleOption[] = [
    {
      label: "Quantile",
      type: "quantile"
    },
    {
      label: "Linear",
      type: "linear"
    }
  ];

  bases: PaintBase[] = [new GemeentePaint(), new ProvinciePaint(), new RegioPaint()];

  constructor(store: Store) {
    this.store = store;
  }

  @computed get colorScale(): ColorScale {
    let r = chroma.limits(this.data, "q", 6);
    let colorSteps = [];
    let lastV = -1;
    r.forEach((v, n) => {
      if (v !== lastV) {
        colorSteps.push(v);
        colorSteps.push(PaintStore.colors[n]);
      }
      lastV = v;
    });
    return colorSteps;
  }

  @computed get scale() {
    let scale = chroma.scale(PaintStore.colors).domain(chroma.limits(this.data, "q", 5));
    return scale;
  }

  getColor(v: number): string {
    return this.scale(v).hex();
  }

  @computed get data() {
    if (this.store.playing) {
      if (this.store.as100k) {
        if (this.store.asIncrement) {
          return this.store.target.map(t => t.lastDelta100k);
        }
        return this.store.target.map(t => t.lastTotal100k);
      }

      if (this.store.asIncrement) {
        return this.store.target.map(t => t.lastDelta);
      }
      return this.store.target.map(t => t.lastTotal);
    }

    if (this.store.as100k) {
      if (this.store.asIncrement) {
        return this.store.target.map(t => t.delta100k);
      }
      return this.store.target.map(t => t.total100k);
    }

    if (this.store.asIncrement) {
      return this.store.target.map(t => t.delta);
    }
    return this.store.target.map(t => t.total);
  }

  @computed get targetPaint() {
    let v = this.store.view;
    let p = this.bases.find(b => b.view === v);
    return p;
  }

  @computed get paints(): Paint {
    let p = this.targetPaint;
    return {
      fillPaint: p.fillPaint(this.colorScale, this.store.targetProperty),
      symbolPaint: p.symbolPaint(this.colorScale, this.store.targetProperty),
      symbolLayout: p.symbolLayout(this.colorScale, this.store.targetProperty)
    };
  }
}
